/**
 * Watch Zone Overlay Component
 *
 * Displays user's watch zones on the map as circles or polygons.
 * Each zone can be toggled on/off for visibility.
 */

import { useMemo } from 'react';
import { Circle, Polygon, Popup, useMap } from 'react-leaflet';
import { WatchZone } from '../api/client';

interface WatchZoneOverlayProps {
  zones: WatchZone[];
  visibleZoneIds: Set<string>;
  onZoneClick?: (zone: WatchZone) => void;
  /** Open the editor for a zone — wired to the popup's "Edit zone" button so
   * users can jump straight from a zone on the map into editing it. */
  onZoneEdit?: (zone: WatchZone) => void;
}

// Zone color based on enabled status. The className 'watch-zone-shape' is
// styled in App.css with `pointer-events: stroke` so taps that land inside
// the zone's fill area (e.g. on an aircraft or fire marker that happens to
// be inside the zone) fall through to the underlying marker. Only the
// border itself remains clickable, which still gives users a way to open
// the zone's popup or trigger onZoneClick.
const ZONE_COLORS = {
  enabled: {
    color: '#22c55e',
    fillColor: '#22c55e',
    fillOpacity: 0.1,
    weight: 2,
    className: 'watch-zone-shape',
  },
  disabled: {
    color: '#6b7280',
    fillColor: '#6b7280',
    fillOpacity: 0.05,
    weight: 1,
    dashArray: '5, 5',
    className: 'watch-zone-shape',
  },
};

// Shared popup body for both circle and polygon zones. `infoLine` is the one
// bit that differs (radius vs. "custom polygon area").
function ZonePopup({
  zone,
  infoLine,
  onZoneEdit,
}: {
  zone: WatchZone;
  infoLine: string;
  onZoneEdit?: (zone: WatchZone) => void;
}) {
  const map = useMap();
  return (
    <Popup className="watch-zone-popup-wrapper">
      <div className="watch-zone-popup">
        <div className="watch-zone-popup-header">
          <span className={`watch-zone-popup-status ${zone.isEnabled ? 'enabled' : 'disabled'}`} />
          <strong>{zone.name}</strong>
        </div>
        <div className="watch-zone-popup-info">
          <span>{infoLine}</span>
        </div>
        <div className="watch-zone-popup-types">
          {zone.preferences.incidentTypes.map(type => (
            <span key={type} className="watch-zone-popup-type">
              {type === 'FIRE_INCIDENT' && '🔥'}
              {type === 'HOTSPOT' && '🛰️'}
              {type === 'ROAD_CLOSURE' && '🚧'}
              {type === 'POWER_OUTAGE' && '⚡'}
            </span>
          ))}
        </div>
        {onZoneEdit && (
          <button
            type="button"
            className="watch-zone-popup-edit"
            onClick={() => {
              // Close the popup so it doesn't obscure the map while editing.
              map.closePopup();
              onZoneEdit(zone);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit zone
          </button>
        )}
      </div>
    </Popup>
  );
}

export default function WatchZoneOverlay({
  zones,
  visibleZoneIds,
  onZoneClick,
  onZoneEdit,
}: WatchZoneOverlayProps) {
  // Filter to only visible zones
  const visibleZones = useMemo(() => {
    return zones.filter(zone => visibleZoneIds.has(zone.zoneId));
  }, [zones, visibleZoneIds]);

  return (
    <>
      {visibleZones.map((zone) => {
        const style = zone.isEnabled ? ZONE_COLORS.enabled : ZONE_COLORS.disabled;

        if (zone.geometry.type === 'circle' && zone.geometry.center) {
          return (
            <Circle
              key={zone.zoneId}
              center={[zone.geometry.center.lat, zone.geometry.center.lon]}
              radius={(zone.geometry.radiusKm || 25) * 1000}
              pathOptions={style}
              eventHandlers={{
                click: () => onZoneClick?.(zone),
              }}
            >
              <ZonePopup
                zone={zone}
                infoLine={`Radius: ${zone.geometry.radiusKm} km`}
                onZoneEdit={onZoneEdit}
              />
            </Circle>
          );
        }

        if (zone.geometry.type === 'polygon' && zone.geometry.coordinates) {
          const positions = zone.geometry.coordinates.map(c => [c.lat, c.lon] as [number, number]);
          return (
            <Polygon
              key={zone.zoneId}
              positions={positions}
              pathOptions={style}
              eventHandlers={{
                click: () => onZoneClick?.(zone),
              }}
            >
              <ZonePopup
                zone={zone}
                infoLine="Custom polygon area"
                onZoneEdit={onZoneEdit}
              />
            </Polygon>
          );
        }

        return null;
      })}
    </>
  );
}
